import { Injectable } from '@angular/core';

export type IconLibrary = 'ri' | 'mdi' | 'bx' | 'la';

export interface IconEntry {
  /** Biblioteca de origem. */
  lib: IconLibrary;
  /** Nome da classe específica (ex.: "ri-home-line", "mdi-home"). */
  name: string;
  /** Classe completa a usar no HTML (ex.: "mdi mdi-home"). */
  cls: string;
  /** Texto de pesquisa, sem prefixo nem hífens. */
  search: string;
}

export const ICON_LIBRARIES: { id: IconLibrary; label: string; reference: string }[] = [
  { id: 'ri', label: 'Remix', reference: 'https://themesbrand.com/velzon/html/default/icons-remix.html' },
  { id: 'mdi', label: 'Material Design', reference: 'https://themesbrand.com/velzon/html/default/icons-materialdesign.html' },
  { id: 'bx', label: 'Boxicons', reference: 'https://themesbrand.com/velzon/html/default/icons-boxicons.html' },
  { id: 'la', label: 'Line Awesome', reference: 'https://themesbrand.com/velzon/html/default/icons-lineawesome.html' },
];

/**
 * Catálogo de ícones disponíveis na aplicação, lido das folhas de estilo já
 * carregadas (Remix, Material Design Icons, Boxicons, Line Awesome). Não há
 * lista estática: o que o CSS tiver é o que aparece.
 */
@Injectable({ providedIn: 'root' })
export class IconCatalogService {
  private entries: IconEntry[] | null = null;
  private names = new Set<string>();

  /** Lista completa; a leitura do CSS é feita uma vez e fica em cache. */
  all(): IconEntry[] {
    if (!this.entries) this.entries = this.scan();
    return this.entries;
  }

  /** Verifica se alguma das classes do valor existe no catálogo. */
  exists(value: string | null | undefined): boolean {
    this.all();
    const tokens = `${value ?? ''}`.trim().split(/\s+/).filter(Boolean);
    return tokens.some((token) => this.names.has(token));
  }

  /** Classe completa para um valor digitado só com o nome (ex.: "mdi-home"). */
  normalize(value: string | null | undefined): string {
    const raw = `${value ?? ''}`.trim();
    if (!raw) return '';
    if (raw.includes(' ')) return raw;
    if (raw.startsWith('mdi-')) return `mdi ${raw}`;
    if (/^bx[sl]?-/.test(raw)) return `bx ${raw}`;
    if (raw.startsWith('la-')) return `las ${raw}`;
    return raw;
  }

  private scan(): IconEntry[] {
    const found = new Map<string, IconEntry>();
    const pattern = /^\.((ri|mdi|bx|bxs|bxl|la)-[a-z0-9-]+)::?before$/;
    const utility = /-(spin|pulse|rotate|flip|ul|li|lg|xs|sm|[1-9]x|fw|border|stack|inverse|tada|flashing|burst|fade|dark|light)(-|$)/;

    for (const sheet of Array.from(document.styleSheets)) {
      let rules: CSSRuleList;
      try {
        rules = (sheet as CSSStyleSheet).cssRules;
      } catch {
        continue; // folha de outra origem
      }
      if (!rules) continue;
      for (const rule of Array.from(rules)) {
        const selector = (rule as CSSStyleRule).selectorText;
        if (!selector) continue;
        for (const part of selector.split(',')) {
          const match = pattern.exec(part.trim());
          if (!match) continue;
          const name = match[1];
          if (utility.test(name)) continue;
          if (found.has(name)) continue;
          const prefix = match[2];
          const lib: IconLibrary = prefix === 'ri' ? 'ri' : prefix === 'mdi' ? 'mdi' : prefix === 'la' ? 'la' : 'bx';
          found.set(name, {
            lib,
            name,
            cls: this.normalize(name),
            search: name.replace(/^(ri|mdi|bxs|bxl|bx|la)-/, '').replace(/-(line|fill)$/, '').replace(/-/g, ' '),
          });
        }
      }
    }

    const entries = Array.from(found.values()).sort((a, b) => a.name.localeCompare(b.name));
    this.names = new Set(entries.map((e) => e.name));
    return entries;
  }
}
